import React, { useEffect, useRef, useState } from "react";
import { Editor } from "@tiptap/react";
import TablePickerPopover from "./TablePickerPopover";
import { FONT_SIZES } from "./extensions/fontSize";

interface BlogEditorToolbarProps {
  editor: Editor | null;
  onInsertImage: () => void;
  onInsertTable: (rows: number, columns: number) => void;
}

const DEFAULT_TEXT_COLOR = "#101828";

type TextStylePreset = {
  label: string;
  description: string;
  fontSize: string;
  block: "paragraph" | "heading";
  level?: 1 | 2 | 3;
};

const TEXT_STYLE_PRESETS: TextStylePreset[] = [
  {
    label: "Heading",
    description: "Large section title",
    fontSize: "32px",
    block: "heading",
    level: 1,
  },
  {
    label: "Sub heading",
    description: "Medium section title",
    fontSize: "24px",
    block: "heading",
    level: 2,
  },
  {
    label: "Section title",
    description: "Small section title",
    fontSize: "18px",
    block: "heading",
    level: 3,
  },
  {
    label: "Body",
    description: "Default paragraph text",
    fontSize: "14px",
    block: "paragraph",
  },
  {
    label: "Caption",
    description: "Small supporting text",
    fontSize: "12px",
    block: "paragraph",
  },
];

const normalizeLinkUrl = (url: string) => {
  const trimmedUrl = url.trim();

  if (!trimmedUrl) {
    return "";
  }

  if (/^(https?:|mailto:|tel:)/i.test(trimmedUrl)) {
    return trimmedUrl;
  }

  return `https://${trimmedUrl}`;
};

const ClearFormatIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path
      d="M9.08333 1.75V0.75H0.75V1.75M4.91667 0.916667V9.08333M4.91667 9.08333H4.08333M4.91667 9.08333H5.75"
      stroke="#101828"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

const BoldIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M4.5 3.16602H8.33333C9.71405 3.16602 10.8333 4.2853 10.8333 5.66602C10.8333 7.04673 9.71405 8.16602 8.33333 8.16602H4.5V3.16602Z"
      stroke="#667085"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="M4.5 8.16602H9.16667C10.4553 8.16602 11.5 9.21068 11.5 10.4993C11.5 11.788 10.4553 12.8327 9.16667 12.8327H4.5V8.16602Z"
      stroke="#667085"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

const ItalicIcon = () => (
  <svg width="8" height="12" viewBox="0 0 8 12" fill="none">
    <path
      d="M4.91667 0.75H3.41667M4.91667 0.75H6.41667M4.91667 0.75L2.25 10.4167M2.25 10.4167H0.75M2.25 10.4167H3.75"
      stroke="#667085"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

const UnderlineIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M3.16797 12.834H12.8346"
      stroke="#667085"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="M10.8346 3.16602V7.33268C10.8346 8.89749 9.56611 10.166 8.0013 10.166C6.4365 10.166 5.16797 8.89749 5.16797 7.33268V3.16602"
      stroke="#667085"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

const StrikeIcon = () => (
  <svg width="12" height="11" viewBox="0 0 12 11" fill="none">
    <path
      d="M0.75 5.58333H10.4167M9.75 2.91667V2.75C9.75 1.64543 8.85457 0.75 7.75 0.75H3.41667C2.3121 0.75 1.41667 1.64543 1.41667 2.75V3.58333C1.41667 4.6879 2.3121 5.58333 3.41667 5.58333H7.58333M1.41667 7.91667V8.25C1.41667 9.35457 2.3121 10.25 3.41667 10.25H7.75C8.85457 10.25 9.75 9.35457 9.75 8.25V7.25"
      stroke="#667085"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

const AlignLeftIcon = () => (
  <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
    <path
      d="M0.75 0.75H7.08334M0.75 9.08333H7.08334M0.75 4.91667H10.4167"
      stroke="#667085"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

const AlignCenterIcon = () => (
  <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
    <path
      d="M2.75 0.75H8.41667M2.75 9.08333H8.41667M0.75 4.91667H10.4167"
      stroke="#667085"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

const AlignRightIcon = () => (
  <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
    <path
      d="M4.08333 0.75H10.4167M4.08333 9.08333H10.4167M0.75 4.91667H10.4167"
      stroke="#667085"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

const OrderedListIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M0.75 1.75L1.75 0.75V4.41667M1.75 4.41667H0.75M1.75 4.41667H2.41667M6.41666 7.08333H5.35416C4.867 7.08333 4.59548 6.54059 4.85386 6.15491C4.88569 6.1074 4.93075 6.07075 4.97836 6.03906L6.17827 5.24063C6.22589 5.20895 6.27079 5.17215 6.3033 5.12509C6.60022 4.69522 6.29752 4.08333 5.75172 4.08333H4.75M8.75 7.41667H9.71426C10.5372 7.41667 10.6832 8.67505 9.9234 8.8871C9.88659 8.89737 9.84803 8.90147 9.80984 8.90281L9.41667 8.91667L9.80984 8.93052C9.84803 8.93187 9.88659 8.93597 9.9234 8.94624C10.6832 9.15829 10.5372 10.4167 9.71426 10.4167H8.75"
      stroke="#667085"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

const BulletListIcon = () => (
  <svg width="10" height="11" viewBox="0 0 10 11" fill="none">
    <path
      d="M3.58333 1.08333H9.25M3.58333 5.08333H9.25M3.58333 9.08333H9.25M1.41667 1.08333C1.41667 1.26743 1.26743 1.41667 1.08333 1.41667C0.899238 1.41667 0.75 1.26743 0.75 1.08333C0.75 0.899238 0.899238 0.75 1.08333 0.75C1.26743 0.75 1.41667 0.899238 1.41667 1.08333ZM1.41667 5.08333C1.41667 5.26743 1.26743 5.41667 1.08333 5.41667C0.899238 5.41667 0.75 5.26743 0.75 5.08333C0.75 4.89924 0.899238 4.75 1.08333 4.75C1.26743 4.75 1.41667 4.89924 1.41667 5.08333ZM1.41667 9.08333C1.41667 9.26743 1.26743 9.41667 1.08333 9.41667C0.899238 9.41667 0.75 9.26743 0.75 9.08333C0.75 8.89924 0.899238 8.75 1.08333 8.75C1.26743 8.75 1.41667 8.89924 1.41667 9.08333Z"
      stroke="#667085"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

const ImageIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M0.75 8.25L2.58079 5.92112C3.09993 5.26074 4.09302 5.23914 4.64039 5.87631L6.25 7.75M4.86003 6.13199C5.55147 5.25244 6.5149 4.00637 6.57757 3.92529L6.58422 3.91678C7.1043 3.26065 8.09422 3.24054 8.64039 3.87631L10.25 5.75M2.08333 0.75H9.08333C9.81971 0.75 10.4167 1.34695 10.4167 2.08333V9.08333C10.4167 9.81971 9.81971 10.4167 9.08333 10.4167H2.08333C1.34695 10.4167 0.75 9.81971 0.75 9.08333V2.08333C0.75 1.34695 1.34695 0.75 2.08333 0.75Z"
      stroke="#667085"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

const LinkIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M8.74509 6.41176L9.57843 5.57843C10.683 4.47386 10.683 2.683 9.57843 1.57843C8.47386 0.473858 6.683 0.473857 5.57843 1.57843L4.74509 2.41176M2.41176 4.74509L1.57843 5.57843C0.473857 6.683 0.473858 8.47386 1.57843 9.57843C2.683 10.683 4.47386 10.683 5.57843 9.57843L6.41176 8.74509M7.07843 4.07843L4.07843 7.07843"
      stroke="#667085"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

const TableIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path
      d="M11.25 2.25V15.75"
      stroke="#00A759"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="M14.25 2.25H3.75C2.92157 2.25 2.25 2.92157 2.25 3.75V14.25C2.25 15.0784 2.92157 15.75 3.75 15.75H14.25C15.0784 15.75 15.75 15.0784 15.75 14.25V3.75C15.75 2.92157 15.0784 2.25 14.25 2.25Z"
      stroke="#00A759"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="M15.75 6.75H2.25"
      stroke="#00A759"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="M15.75 11.25H2.25"
      stroke="#00A759"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

const ToolbarButton = ({
  active = false,
  children,
  label,
  onClick,
}: {
  active?: boolean;
  children: React.ReactNode;
  label: string;
  onClick: () => void;
}) => (
  <button
    aria-label={label}
    className={`blog-toolbar-button ${active ? "active" : ""}`}
    data-tooltip={label}
    onMouseDown={(event) => event.preventDefault()}
    onClick={onClick}
    type="button"
  >
    {children}
  </button>
);

const BlogEditorToolbar: React.FC<BlogEditorToolbarProps> = ({
  editor,
  onInsertImage,
  onInsertTable,
}) => {
  const tablePickerRef = useRef<HTMLDivElement | null>(null);
  const linkPopoverRef = useRef<HTMLDivElement | null>(null);
  const stylePopoverRef = useRef<HTMLDivElement | null>(null);
  const colorInputRef = useRef<HTMLInputElement | null>(null);
  const [isTablePickerOpen, setIsTablePickerOpen] = useState(false);
  const [isLinkPopoverOpen, setIsLinkPopoverOpen] = useState(false);
  const [isStylePopoverOpen, setIsStylePopoverOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const textStyleAttributes = editor?.getAttributes("textStyle") || {};
  const selectedSize =
    typeof textStyleAttributes.fontSize === "string"
      ? textStyleAttributes.fontSize
      : "14px";
  const selectedColor =
    typeof textStyleAttributes.color === "string"
      ? textStyleAttributes.color
      : DEFAULT_TEXT_COLOR;
  const isAlignLeftActive =
    editor?.isActive({ textAlign: "left" }) ||
    (!editor?.isActive({ textAlign: "center" }) &&
      !editor?.isActive({ textAlign: "right" }));

  useEffect(() => {
    if (!isTablePickerOpen && !isLinkPopoverOpen && !isStylePopoverOpen) return;

    const handleDocumentMouseDown = (event: MouseEvent) => {
      if (
        isStylePopoverOpen &&
        stylePopoverRef.current &&
        !stylePopoverRef.current.contains(event.target as Node)
      ) {
        setIsStylePopoverOpen(false);
      }

      if (
        isTablePickerOpen &&
        tablePickerRef.current &&
        !tablePickerRef.current.contains(event.target as Node)
      ) {
        setIsTablePickerOpen(false);
      }

      if (
        isLinkPopoverOpen &&
        linkPopoverRef.current &&
        !linkPopoverRef.current.contains(event.target as Node)
      ) {
        setIsLinkPopoverOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);

    return () => {
      document.removeEventListener("mousedown", handleDocumentMouseDown);
    };
  }, [isLinkPopoverOpen, isStylePopoverOpen, isTablePickerOpen]);

  const openStylePopover = () => {
    setIsLinkPopoverOpen(false);
    setIsTablePickerOpen(false);
    setIsStylePopoverOpen((isOpen) => !isOpen);
  };

  const openLinkPopover = () => {
    setIsStylePopoverOpen(false);
    setIsTablePickerOpen(false);
    setLinkUrl(editor?.getAttributes("link").href || "");
    setIsLinkPopoverOpen((isOpen) => !isOpen);
  };

  const applyTextStylePreset = (preset: TextStylePreset) => {
    const commandChain = editor?.chain().focus();

    if (!commandChain) {
      return;
    }

    if (preset.block === "heading" && preset.level) {
      commandChain.setHeading({ level: preset.level }).setFontSize(preset.fontSize).run();
    } else {
      commandChain.setParagraph().setFontSize(preset.fontSize).run();
    }

    setIsStylePopoverOpen(false);
  };

  const isTextStylePresetActive = (preset: TextStylePreset) => {
    const hasMatchingSize =
      (typeof textStyleAttributes.fontSize === "string"
        ? textStyleAttributes.fontSize
        : "14px") === preset.fontSize;

    if (preset.block === "heading") {
      return !!preset.level && editor?.isActive("heading", { level: preset.level }) && hasMatchingSize;
    }

    return editor?.isActive("paragraph") && hasMatchingSize;
  };

  const handleInsertLink = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedUrl = normalizeLinkUrl(linkUrl);

    if (!normalizedUrl) {
      return;
    }

    editor
      ?.chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: normalizedUrl })
      .run();
    setIsLinkPopoverOpen(false);
  };

  const handleRemoveLink = () => {
    editor?.chain().focus().extendMarkRange("link").unsetLink().run();
    setIsLinkPopoverOpen(false);
    setLinkUrl("");
  };

  return (
    <div className="blog-editor-toolbar">
      <div className="blog-toolbar-group size-group">
        <div className="blog-toolbar-size-wrap" data-tooltip="Font size">
          <span className="blog-toolbar-size-value">
            {(FONT_SIZES.includes(selectedSize) ? selectedSize : "14px").replace(
              "px",
              "",
            )}
          </span>
          <select
            aria-label="Font size"
            className="blog-toolbar-size"
            onChange={(event) =>
              editor?.chain().focus().setFontSize(event.target.value).run()
            }
            value={FONT_SIZES.includes(selectedSize) ? selectedSize : "14px"}
          >
            {FONT_SIZES.map((size) => (
              <option key={size} value={size}>
                {size.replace("px", "")}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="blog-toolbar-group">
        <div className="blog-toolbar-style-wrap" ref={stylePopoverRef}>
          <ToolbarButton label="Text styles" onClick={openStylePopover}>
            <ClearFormatIcon />
          </ToolbarButton>
          {isStylePopoverOpen && (
            <div className="style-picker-popover">
              <h4>Text styles</h4>
              <div className="style-picker-list">
                {TEXT_STYLE_PRESETS.map((preset) => (
                  <button
                    key={preset.label}
                    className={`style-picker-option ${
                      isTextStylePresetActive(preset) ? "active" : ""
                    }`}
                    onClick={() => applyTextStylePreset(preset)}
                    type="button"
                  >
                    <span
                      className="style-picker-preview"
                      style={{ fontSize: preset.fontSize }}
                    >
                      {preset.label}
                    </span>
                    <span className="style-picker-row-action" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="blog-toolbar-color-wrap">
          <ToolbarButton
            active={!!textStyleAttributes.color}
            label="Text color"
            onClick={() => colorInputRef.current?.click()}
          >
            <span
              className="blog-toolbar-color-dot"
              style={{ background: selectedColor }}
            />
          </ToolbarButton>
          <input
            ref={colorInputRef}
            type="color"
            aria-label="Pick text color"
            className="blog-toolbar-color-input"
            value={selectedColor}
            onChange={(event) =>
              editor?.chain().focus().setColor(event.target.value).run()
            }
          />
        </div>
      </div>

      <div className="blog-toolbar-group">
        <ToolbarButton
          active={!!editor?.isActive("bold")}
          label="Bold"
          onClick={() => editor?.chain().focus().toggleBold().run()}
        >
          <BoldIcon />
        </ToolbarButton>
        <ToolbarButton
          active={!!editor?.isActive("italic")}
          label="Italic"
          onClick={() => editor?.chain().focus().toggleItalic().run()}
        >
          <ItalicIcon />
        </ToolbarButton>
        <ToolbarButton
          active={!!editor?.isActive("underline")}
          label="Underline"
          onClick={() => editor?.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon />
        </ToolbarButton>
        <ToolbarButton
          active={!!editor?.isActive("strike")}
          label="Strikethrough"
          onClick={() => editor?.chain().focus().toggleStrike().run()}
        >
          <StrikeIcon />
        </ToolbarButton>
      </div>

      <div className="blog-toolbar-group">
        <ToolbarButton
          active={!!isAlignLeftActive}
          label="Align left"
          onClick={() => editor?.chain().focus().setTextAlign("left").run()}
        >
          <AlignLeftIcon />
        </ToolbarButton>
        <ToolbarButton
          active={!!editor?.isActive({ textAlign: "center" })}
          label="Align center"
          onClick={() => editor?.chain().focus().setTextAlign("center").run()}
        >
          <AlignCenterIcon />
        </ToolbarButton>
        <ToolbarButton
          active={!!editor?.isActive({ textAlign: "right" })}
          label="Align right"
          onClick={() => editor?.chain().focus().setTextAlign("right").run()}
        >
          <AlignRightIcon />
        </ToolbarButton>
      </div>

      <div className="blog-toolbar-group">
        <ToolbarButton
          active={!!editor?.isActive("orderedList")}
          label="Ordered list"
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
        >
          <OrderedListIcon />
        </ToolbarButton>
        <ToolbarButton
          active={!!editor?.isActive("bulletList")}
          label="Bullet list"
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
        >
          <BulletListIcon />
        </ToolbarButton>
      </div>

      <div className="blog-toolbar-group">
        <ToolbarButton label="Insert image" onClick={onInsertImage}>
          <ImageIcon />
        </ToolbarButton>
        <div className="blog-toolbar-link-wrap" ref={linkPopoverRef}>
          <ToolbarButton
            active={!!editor?.isActive("link")}
            label="Insert link"
            onClick={openLinkPopover}
          >
            <LinkIcon />
          </ToolbarButton>
          {isLinkPopoverOpen && (
            <form className="link-picker-popover" onSubmit={handleInsertLink}>
              <h4>Insert link</h4>
              <input
                aria-label="Link URL"
                autoFocus
                className="link-picker-input"
                onChange={(event) => setLinkUrl(event.target.value)}
                placeholder="https://example.com"
                type="text"
                value={linkUrl}
              />
              <div className="link-picker-actions">
                {editor?.isActive("link") && (
                  <button
                    className="link-picker-remove"
                    onClick={handleRemoveLink}
                    type="button"
                  >
                    Remove
                  </button>
                )}
                <button className="link-picker-insert" type="submit">
                  Add link
                </button>
              </div>
            </form>
          )}
        </div>
        <div className="blog-toolbar-table-wrap" ref={tablePickerRef}>
          <button
            aria-label="Insert table"
            className="blog-toolbar-button table-button"
            data-tooltip="Insert table"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              setIsStylePopoverOpen(false);
              setIsLinkPopoverOpen(false);
              setIsTablePickerOpen((isOpen) => !isOpen);
            }}
            type="button"
          >
            <span className="new-badge">NEW</span>
            <TableIcon />
          </button>
          {isTablePickerOpen && (
            <TablePickerPopover
              onInsert={(rows, columns) => {
                onInsertTable(rows, columns);
                setIsTablePickerOpen(false);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogEditorToolbar;
